import React, { ReactElement } from 'react';
import Link from 'next/link';
import { SpriteIcon } from '@/components/ui/SvgSprite';
import { ArrowDown2 } from 'iconsax-react';
import { LayoutProps } from '@/types/generalSettings';
import BrokersAd = LayoutProps.BrokersAd;
import { Crown } from '@/components/Icons';
import MegaMenu from './MegaMenu';

import type { NewMenuTypes } from '@/types/layout/menu';
import { getIconByString } from '@/components/layout/MenuIconHelper';
// Ensure the type matches the new structure with parentId
interface ExtendedMenuItem extends LayoutProps.MenuItem {
    parentId?: string | null;
    newMenuIconBox?: NewMenuTypes.MenuIconBox;
}

export interface ChildItem {
    node?: ExtendedMenuItem;
}

interface NavDesktopProps {
    menuItems?: ExtendedMenuItem[]; // Using the extended type here
    setOpen: (state: boolean) => void;
    brokerAd?: BrokersAd[];
    exchangeForeignAd?: BrokersAd[];
    exchangeIranianAd?: BrokersAd[];
    propAd?: BrokersAd[];
    megaMenuContent?: any; // FIX: Accept content prop
}

const NavDesktopNew: React.FC<NavDesktopProps> = ({
    setOpen = () => {},
    menuItems,
    megaMenuContent,
    brokerAd,
    exchangeForeignAd,
    exchangeIranianAd,
    propAd,
}) => {
    if (!Array.isArray(menuItems)) {
        return null;
    }

    const topLevelItems = menuItems.filter((item) => !item.parentId);

    const renderNormalDropdown = (edges: ChildItem[]): ReactElement[] => {
        return edges.map((edge, index) => {
            const childItem = edge.node;
            if (!childItem) return <React.Fragment key={index} />;
            const isCrownItem = childItem?.newMenuIconBox?.isCta === true;
            return (
                <li key={childItem.databaseId || index} className='w-full'>
                    <Link
                        href={childItem.uri || '#'}
                        prefetch={false}
                        onClick={() => setOpen(false)}
                        className={`group/item flex items-center justify-between w-full px-4 py-2.5 text-xs rounded-lg transition-colors ${
                            isCrownItem
                                ? 'bg-[#EEAC43] px-3 py-2 rounded-2xl text-primary'
                                : 'text-gray-700 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-progress dark:hover:text-white font-light'
                        }`}
                    >
                        {isCrownItem ? (
                            <div className={'flex items-center gap-1'}>
                                {isCrownItem && <Crown className={'text-primary'} />}
                                {childItem.label}
                            </div>
                        ) : (
                            childItem.label
                        )}
                        <SpriteIcon
                            name='arrow-left-s'
                            className={
                                'transition-transform duration-200 group-hover/item:-translate-x-1'
                            }
                            size={14}
                        />
                    </Link>
                </li>
            );
        });
    };

    const renderMenuItems = (items: ExtendedMenuItem[]): ReactElement[] => {
        return items.map((item, index) => {
            const key = item.databaseId || `menu-item-${index}`;
            const hasChildren = item.childItems && item.childItems.edges.length > 0;
            const isMegaMenuTarget = item?.newMenuIconBox?.megaMenu === true;

            const isCrownItem = item?.newMenuIconBox?.isCta === true;

            // دریافت آیکون مربوطه
            const icon = getIconByString(
                item.newMenuIconBox?.menuIcon,
                'size-4 text-gray-500 group-hover:text-progress dark:text-gray-400 transition-colors',
            );

            // 1. MEGA MENU ITEM (بهترین ها)
            if (isMegaMenuTarget && hasChildren) {
                return (
                    <li
                        key={key}
                        className='py-4 flex gap-x-1 text-xs text-nowrap group items-center font-light relative'
                    >
                        <Link
                            href={item.uri || '#'}
                            prefetch={false}
                            className='flex items-center gap-1.5 group-hover:text-progress transition-colors cursor-default'
                        >
                            {/* نمایش آیکون */}
                            {icon}
                            {item.label}
                            <ArrowDown2
                                className='group-hover:rotate-180 stroke-current transition-transform duration-300'
                                size={12}
                            />
                        </Link>

                        <div className='absolute top-full -right-[350px] hidden group-hover:block z-50'>
                            {/* 2. Animation Wrapper: Handles fade/slide without moving horizontally */}
                            <div className='animate-in fade-in slide-in-from-top-2 duration-200'>
                                {/* FIX: Pass content to MegaMenu */}
                                <MegaMenu
                                    item={item}
                                    content={megaMenuContent}
                                    brokerAd={brokerAd}
                                    exchangeForeignAd={exchangeForeignAd}
                                    exchangeIranianAd={exchangeIranianAd}
                                    propAd={propAd}
                                />
                            </div>
                        </div>
                    </li>
                );
            }

            // 2. NORMAL DROPDOWN ITEMS
            if (hasChildren) {
                return (
                    <li
                        key={key}
                        className='py-4 flex gap-x-1 text-xs text-nowrap group items-center font-light relative'
                    >
                        <Link
                            href={item.uri || '#'}
                            prefetch={false}
                            className='flex items-center gap-1.5 group-hover:text-progress transition-colors'
                        >
                            {icon}
                            {item.label}
                            <ArrowDown2
                                className='group-hover:rotate-180 stroke-current transition-transform duration-300'
                                size={12}
                            />
                        </Link>

                        <ul
                            id={`menu-item-${key}`}
                            className='absolute top-full -right-1/2 pt-2 border border-gray-100 dark:border-white/10 hidden flex-col bg-white p-2 dark:bg-primary-active rounded-xl min-w-[240px] shadow-xl group-hover:flex z-40 animate-in fade-in slide-in-from-top-1 duration-200'
                        >
                            {renderNormalDropdown(item.childItems?.edges || [])}
                        </ul>
                    </li>
                );
            }

            // 3. SINGLE LINK ITEMS
            return (
                <li
                    key={key}
                    className='py-2 text-nowrap group relative font-light flex items-center justify-center'
                >
                    <Link
                        className={`text-xs cursor-pointer flex gap-x-1.5 items-center transition-all  ${isCrownItem ? 'bg-[#EEAC43] hover:scale-105 px-3 py-2 rounded-2xl dark:text-primary' : 'font-light'}`}
                        href={item.uri || '#'}
                        prefetch={false}
                        title={item.label}
                    >
                        {isCrownItem && <Crown className={'text-primary'} />}
                        {!isCrownItem && icon}
                        {item.label}
                    </Link>
                </li>
            );
        });
    };

    return (
        <nav className={'mx-auto flex items-center xl:pr-4 max-xl:hidden'}>
            <ul
                dir={'rtl'}
                className={
                    'flex flex-1 relative list-none items-stretch justify-center space-x-1 gap-3'
                }
            >
                {renderMenuItems(topLevelItems)}
            </ul>
        </nav>
    );
};

export default NavDesktopNew;
